'use client';

import { useCallback, useMemo, useState, type KeyboardEvent } from 'react';
import { Autocomplete, Box, Button, Chip, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterList from '@mui/icons-material/FilterList';
import type { SimulationDataSetDto } from '@/api/ApiClient';
import { splitCompoundIntoElements } from '@/components/tables/reaxff/reaxffUtils';
import { ReaxffElementFilterDialog } from '@/components/dialog/ReaxffElementFilterDialog';

export type ReaxffAppliedFilters = {
  selectedElements: string[];
  year: number | null;
  author: string | null;
  showUnpublished: boolean;
};

export const defaultReaxffAppliedFilters = (): ReaxffAppliedFilters => ({
  selectedElements: [],
  year: null,
  author: null,
  showUnpublished: false,
});

type ReaxffSearchPanelProps = {
  /** Title shown in the header row (matches other search panels). */
  title?: string;
  appliedFilters: ReaxffAppliedFilters;
  onApply: (filters: ReaxffAppliedFilters) => void;
  onReset: () => void;
  yearOptions: number[];
  authorOptions: string[];
  /** Full dataset rows (for element availability and “remaining” hints in the element picker). */
  data: SimulationDataSetDto[];
  canUpload: boolean;
  canViewUnpublished: boolean;
  onUpload: () => void;
  onExportCsv: () => void;
};

export function ReaxffSearchPanel({
  title = 'ReaxFF Force Fields',
  appliedFilters,
  onApply,
  onReset,
  yearOptions,
  authorOptions,
  data,
  canUpload,
  canViewUnpublished,
  onUpload,
  onExportCsv,
}: ReaxffSearchPanelProps) {
  /** Unsaved edits only. `null` = follow parent `appliedFilters` (no mirror effect). */
  const [filterDraft, setFilterDraft] = useState<ReaxffAppliedFilters | null>(null);
  const localFilters = filterDraft ?? appliedFilters;

  const setLocalFilters = useCallback(
    (next: ReaxffAppliedFilters | ((prev: ReaxffAppliedFilters) => ReaxffAppliedFilters)) => {
      setFilterDraft((prevDraft) => {
        const base = prevDraft ?? appliedFilters;
        return typeof next === 'function' ? (next as (p: ReaxffAppliedFilters) => ReaxffAppliedFilters)(base) : next;
      });
    },
    [appliedFilters]
  );

  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);

  const availableElements = useMemo(() => {
    const elements = new Set<string>();
    data.forEach((item) => {
      const itemElements = item.elements.split(',').flatMap((compound) => splitCompoundIntoElements(compound.trim()));
      itemElements.forEach((el) => elements.add(el));
    });
    return elements;
  }, [data]);

  const remainingElements = useMemo(() => {
    if (localFilters.selectedElements.length === 0) return availableElements;

    const remaining = new Set<string>();
    data.forEach((item) => {
      const itemElements = item.elements.split(',').flatMap((compound) => splitCompoundIntoElements(compound.trim()));
      if (localFilters.selectedElements.every((selected) => itemElements.includes(selected))) {
        itemElements.forEach((el) => remaining.add(el));
      }
    });
    return remaining;
  }, [data, localFilters.selectedElements, availableElements]);

  const handleApply = useCallback(() => {
    onApply(localFilters);
    setFilterDraft(null);
  }, [localFilters, onApply]);

  const handleReset = useCallback(() => {
    onReset();
    setFilterDraft(null);
  }, [onReset]);

  const handleSearchOnEnter = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      handleApply();
    },
    [handleApply]
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2, mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          {title}
        </Typography>
        <Box display="flex" gap={1} sx={{ ml: 'auto' }} flexWrap="wrap" justifyContent="flex-end">
          {canUpload ? (
            <Button variant="outlined" size="medium" startIcon={<UploadIcon />} onClick={onUpload}>
              Upload
            </Button>
          ) : null}
          <Button variant="outlined" size="medium" startIcon={<FileDownloadIcon />} onClick={onExportCsv}>
            Export
          </Button>
        </Box>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        onKeyDown={handleSearchOnEnter}
        sx={{
          mb: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          py: 1,
          borderRadius: 1,
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<FilterList />}
              onClick={() => setIsMaterialDialogOpen(true)}
              variant="outlined"
              size="small"
              sx={{
                height: 40,
                minHeight: 40,
                boxSizing: 'border-box',
              }}
            >
              Material
            </Button>
            {localFilters.selectedElements.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {localFilters.selectedElements.map((el) => (
                  <Chip
                    key={el}
                    label={el}
                    size="small"
                    onDelete={() =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        selectedElements: prev.selectedElements.filter((e) => e !== el),
                      }))
                    }
                  />
                ))}
              </Box>
            ) : null}
          </Box>

          <Autocomplete
            size="small"
            options={yearOptions}
            value={localFilters.year}
            onChange={(_, newValue) => setLocalFilters((prev) => ({ ...prev, year: newValue }))}
            getOptionLabel={(option) => option?.toString() ?? ''}
            renderInput={(params) => <TextField {...params} label="Year" sx={{ minWidth: 120 }} />}
          />

          <Autocomplete
            size="small"
            options={authorOptions}
            value={localFilters.author}
            onChange={(_, newValue) => setLocalFilters((prev) => ({ ...prev, author: newValue }))}
            getOptionLabel={(option) => option ?? ''}
            renderInput={(params) => <TextField {...params} label="Author" sx={{ minWidth: 200 }} />}
          />

          {canViewUnpublished ? (
            <FormControlLabel
              control={
                <Switch
                  checked={localFilters.showUnpublished}
                  onChange={(e) => setLocalFilters((prev) => ({ ...prev, showUnpublished: e.target.checked }))}
                />
              }
              label="Show Unpublished"
            />
          ) : null}
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={1.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flexShrink: 0 }}
        >
          <Button variant="contained" onClick={handleApply} sx={{ width: 120 }} size="small">
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ width: 90 }} size="small" color="error">
            Reset
          </Button>
        </Box>
      </Box>

      <ReaxffElementFilterDialog
        key={[...localFilters.selectedElements].sort().join()}
        open={isMaterialDialogOpen}
        onClose={() => setIsMaterialDialogOpen(false)}
        onApply={(selectedElements) => setLocalFilters((prev) => ({ ...prev, selectedElements }))}
        selectedElements={localFilters.selectedElements}
        availableElements={availableElements}
        remainingElements={remainingElements}
      />
    </Box>
  );
}
